import { HabitSchedule, ScheduleEntryState, Timestamp } from '@griply/types';

import { getTimeframeCompletions } from './get-timeframe-completions';
import { getTimeframeDayEntries } from './get-timeframe-day-entries';
import { getTimeframeSchedules } from './schedules/get-timeframe-schedules';

export type GetTimeframeProgressOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export type TimeframeProgress = {
  /** What the habit logged in the range. `undefined` when no schedule governs the range at all. */
  completionsCount: number | undefined;
  /** What it was supposed to log. `undefined` for the same reason. */
  target: number | undefined;
};

/*
 * The "x out of y this week" of a habit, for any range.
 *
 * The target comes from the day targets, not from a per-range multiplication: a habit that changes
 * schedule mid-range then counts each day against the schedule that owns that day. Reading
 * `occurrences × completionTarget.count` off the last schedule instead makes every day in the range
 * inherit the last schedule's target, which is wrong the moment the habit was edited mid-week.
 *
 * Frequency schedules (3× per week and friends) have no per-day target to sum — their target is the
 * frequency count itself.
 */
export const getTimeframeProgress = (
  schedules: HabitSchedule[],
  { startDate, endDate }: GetTimeframeProgressOptions,
): TimeframeProgress => {
  // Sorted, not stored order: normalizeSchedules only sorts when it caps something, so a habit whose
  // schedules were appended out of order would read its target off the wrong one.
  const timeframeSchedules = [
    ...getTimeframeSchedules(schedules, { startDate, endDate }),
  ].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  const lastSchedule = timeframeSchedules[timeframeSchedules.length - 1];
  if (!lastSchedule) {
    return { completionsCount: undefined, target: undefined };
  }

  const { timeframeCompletions } = getTimeframeCompletions(schedules, {
    startDate,
    endDate,
  });

  if (lastSchedule.frequency) {
    return {
      completionsCount: timeframeCompletions.reduce(
        (acc, { state, value }) =>
          acc + (state === ScheduleEntryState.Complete && !!value ? value : 0),
        0,
      ),
      target: lastSchedule.frequency.count ?? 1,
    };
  }

  const { timeframeDateEntries } = getTimeframeDayEntries(schedules, {
    startDate,
    endDate,
  });

  return {
    completionsCount: timeframeCompletions.reduce(
      (acc, { value }) => acc + (value ?? 0),
      0,
    ),
    target: timeframeDateEntries.reduce(
      (acc, entry) => acc + (entry.target ?? 0),
      0,
    ),
  };
};
